import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, attendanceApi, payrollApi, leavesApi } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Users, 
  Clock, 
  Calendar, 
  IndianRupee, 
  TrendingUp, 
  UserPlus,
  CheckCircle2,
  XCircle,
  Play,
  Square,
  ArrowRight,
  Building2,
  FileText,
  CalendarDays,
  Briefcase,
  Award,
  Timer
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend, LineChart, Line } from 'recharts';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const COLORS = ['#1a365d', '#2dd4bf', '#22C55E', '#14b8a6', '#8B5CF6', '#EC4899'];

export default function DashboardPage() {
  const { user, isHRManager, isEmployee, canAccessPayroll, canAccessReports } = useAuth();
  const [stats, setStats] = useState(null);
  const [attendanceTrend, setAttendanceTrend] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockLoading, setClockLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Employee-specific state
  const [employeeStats, setEmployeeStats] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [recentPayslips, setRecentPayslips] = useState([]);
  const [myAttendance, setMyAttendance] = useState([]);

  useEffect(() => {
    if (isEmployee) {
      fetchEmployeeDashboardData();
    } else {
      fetchDashboardData();
    }
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, [isEmployee]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, trendRes, todayRes] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getAttendanceTrend(14),
        attendanceApi.getToday()
      ]);
      setStats(statsRes.data);
      setAttendanceTrend(trendRes.data);
      setTodayAttendance(todayRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeDashboardData = async () => {
    try {
      const [todayRes, balanceRes] = await Promise.all([
        attendanceApi.getToday(),
        leavesApi.getBalance(user?.employee_id).catch(() => ({ data: null }))
      ]);
      
      setTodayAttendance(todayRes.data);
      setLeaveBalance(balanceRes.data);
      
      // Fetch recent payslips for employee
      try {
        const payslipRes = await payrollApi.getAll({ limit: 3 });
        setRecentPayslips(payslipRes.data?.slice(0, 3) || []);
      } catch (e) {
        console.log('Could not fetch payslips');
      }
      
      // Fetch my attendance for this month
      try {
        const month = new Date().getMonth() + 1;
        const year = new Date().getFullYear();
        const attendanceRes = await attendanceApi.getAll({ month, year });
        const myRecords = attendanceRes.data?.filter(a => a.employee_id === user?.employee_id) || [];
        setMyAttendance(myRecords);
        
        // Calculate employee stats from attendance
        const presentDays = myRecords.filter(a => a.status === 'present').length;
        const absentDays = myRecords.filter(a => a.status === 'absent').length;
        const leaveDays = myRecords.filter(a => a.status === 'on_leave').length;
        const totalHours = myRecords.reduce((sum, a) => sum + (a.work_hours || 0), 0);
        
        setEmployeeStats({
          presentDays,
          absentDays,
          leaveDays,
          totalHours: Math.round(totalHours),
          avgHours: presentDays > 0 ? (totalHours / presentDays).toFixed(1) : 0
        });
      } catch (e) {
        console.log('Could not fetch attendance');
      }
    } catch (error) {
      console.error('Failed to fetch employee dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    setClockLoading(true);
    try {
      const response = await attendanceApi.clockIn();
      toast.success(`Clocked in at ${response.data.time}`);
      setTodayAttendance(prev => ({ ...prev, clocked_in: true, clock_in_time: response.data.time }));
      // Refresh stats after clock-in
      if (!isEmployee) {
        fetchDashboardData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    } finally {
      setClockLoading(false);
    }
  };

  const handleClockOut = async () => {
    setClockLoading(true);
    try {
      const response = await attendanceApi.clockOut();
      toast.success(`Clocked out. Worked ${response.data.work_hours} hours`);
      setTodayAttendance(prev => ({ 
        ...prev, 
        clocked_out: true, 
        clock_out_time: response.data.time,
        work_hours: response.data.work_hours 
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    } finally {
      setClockLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const deptData = stats?.department_distribution 
    ? Object.entries(stats.department_distribution).map(([name, value]) => ({ name, value }))
    : [];

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="h-10 w-64 bg-muted rounded-lg animate-shimmer" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-36 bg-muted rounded-2xl animate-shimmer" />
          ))}
        </div>
      </div>
    );
  }

  // EMPLOYEE DASHBOARD
  if (isEmployee) {
    return (
      <div className="space-y-8" data-testid="employee-dashboard">
        {/* Header */}
        <div className="page-header flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-muted-foreground mb-1">{getGreeting()}</p>
            <h1 className="page-title">{user?.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">Employee Self-Service Portal</p>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold clock-display text-foreground">
              {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </p>
            <p className="text-muted-foreground">
              {currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
        </div>

        {/* Clock-in/out card */}
        <Card className="relative overflow-hidden border-0 bg-gradient-to-r from-[#0f2744] to-[#1a365d] text-white">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#14b8a6]/10 rounded-full translate-y-1/2 -translate-x-1/4" />
          <CardContent className="relative py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <div className="h-20 w-20 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                  <Clock className="h-10 w-10 text-[#14b8a6]" />
                </div>
                <div>
                  <p className="text-white/70 text-sm mb-1">Today's Attendance</p>
                  {todayAttendance?.clocked_in ? (
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-sm text-white/60">Clock In</p>
                        <p className="text-xl font-semibold">{todayAttendance.clock_in_time}</p>
                      </div>
                      {todayAttendance?.clocked_out && (
                        <>
                          <div className="h-8 w-px bg-white/20" />
                          <div>
                            <p className="text-sm text-white/60">Clock Out</p>
                            <p className="text-xl font-semibold">{todayAttendance.clock_out_time}</p>
                          </div>
                          <div className="h-8 w-px bg-white/20" />
                          <div>
                            <p className="text-sm text-white/60">Hours Worked</p>
                            <p className="text-xl font-semibold">{todayAttendance.work_hours}h</p>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <p className="text-2xl font-semibold">Not clocked in yet</p>
                  )}
                </div>
              </div>
              <div>
                {!todayAttendance?.clocked_in ? (
                  <Button 
                    onClick={handleClockIn} 
                    disabled={clockLoading}
                    className="h-14 px-8 rounded-xl bg-white text-[#1a365d] hover:bg-white/90 font-semibold gap-2"
                    data-testid="clock-in-btn"
                  >
                    <Play className="h-5 w-5" />
                    Clock In
                  </Button>
                ) : !todayAttendance?.clocked_out ? (
                  <Button 
                    onClick={handleClockOut} 
                    disabled={clockLoading}
                    className="h-14 px-8 rounded-xl bg-[#14b8a6] text-[#1A2433] hover:bg-[#14b8a6]/90 font-semibold gap-2"
                    data-testid="clock-out-btn"
                  >
                    <Square className="h-5 w-5" />
                    Clock Out
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-500/20 text-emerald-100">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-semibold">Day Complete</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Employee Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="stat-card border-l-4 border-l-emerald-500">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Days Present</p>
                  <p className="text-4xl font-bold mt-2 text-emerald-600">{employeeStats?.presentDays || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">This month</p>
                </div>
                <div className="h-12 w-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card border-l-4 border-l-amber-500">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Leave Taken</p>
                  <p className="text-4xl font-bold mt-2 text-amber-600">{employeeStats?.leaveDays || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">This month</p>
                </div>
                <div className="h-12 w-12 rounded-2xl bg-amber-500/10 flex items-center justify-center">
                  <CalendarDays className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card border-l-4 border-l-[#1a365d]">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Hours Worked</p>
                  <p className="text-4xl font-bold mt-2 text-[#1a365d]">{employeeStats?.totalHours || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Avg: {employeeStats?.avgHours || 0}h/day</p>
                </div>
                <div className="h-12 w-12 rounded-2xl bg-[#1a365d]/10 flex items-center justify-center">
                  <Timer className="h-6 w-6 text-[#1a365d]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card border-l-4 border-l-purple-500">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Absent Days</p>
                  <p className="text-4xl font-bold mt-2 text-purple-600">{employeeStats?.absentDays || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">This month</p>
                </div>
                <div className="h-12 w-12 rounded-2xl bg-purple-500/10 flex items-center justify-center">
                  <XCircle className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Leave Balance & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Leave Balance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CalendarDays className="h-5 w-5 text-[#1a365d]" />
                Leave Balance
              </CardTitle>
              <CardDescription>Your available leave days</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {leaveBalance ? (
                <>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Casual Leave</span>
                      <span className="font-semibold">{leaveBalance.casual?.total - leaveBalance.casual?.used || 0} / {leaveBalance.casual?.total || 12}</span>
                    </div>
                    <Progress 
                      value={((leaveBalance.casual?.total - leaveBalance.casual?.used) / leaveBalance.casual?.total) * 100} 
                      className="h-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Sick Leave</span>
                      <span className="font-semibold">{leaveBalance.sick?.total - leaveBalance.sick?.used || 0} / {leaveBalance.sick?.total || 12}</span>
                    </div>
                    <Progress 
                      value={((leaveBalance.sick?.total - leaveBalance.sick?.used) / leaveBalance.sick?.total) * 100}
                      className="h-2 [&>div]:bg-red-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Earned Leave</span>
                      <span className="font-semibold">{leaveBalance.earned?.total - leaveBalance.earned?.used || 0} / {leaveBalance.earned?.total || 15}</span>
                    </div>
                    <Progress 
                      value={((leaveBalance.earned?.total - leaveBalance.earned?.used) / leaveBalance.earned?.total) * 100}
                      className="h-2 [&>div]:bg-green-500"
                    />
                  </div>
                </>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  <p>Leave balance not available</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Briefcase className="h-5 w-5 text-[#1a365d]" />
                Quick Actions
              </CardTitle>
              <CardDescription>Frequently used actions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <a
                  href="/leaves"
                  className="flex flex-col items-center justify-center gap-3 p-6 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 hover:shadow-md transition-all"
                >
                  <Calendar className="h-8 w-8 text-amber-600" />
                  <span className="text-sm font-medium">Apply Leave</span>
                </a>
                <a
                  href="/attendance"
                  className="flex flex-col items-center justify-center gap-3 p-6 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 hover:shadow-md transition-all"
                >
                  <Clock className="h-8 w-8 text-blue-600" />
                  <span className="text-sm font-medium">My Attendance</span>
                </a>
                <a
                  href="/payroll"
                  className="flex flex-col items-center justify-center gap-3 p-6 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 hover:shadow-md transition-all"
                >
                  <FileText className="h-8 w-8 text-green-600" />
                  <span className="text-sm font-medium">My Payslips</span>
                </a>
                <a
                  href="/profile"
                  className="flex flex-col items-center justify-center gap-3 p-6 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 hover:shadow-md transition-all"
                >
                  <Award className="h-8 w-8 text-purple-600" />
                  <span className="text-sm font-medium">My Profile</span>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Payslips */}
        {recentPayslips.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <IndianRupee className="h-5 w-5 text-[#1a365d]" />
                    Recent Payslips
                  </CardTitle>
                  <CardDescription>Your latest salary information</CardDescription>
                </div>
                <a href="/payroll" className="text-sm text-[#1a365d] hover:underline flex items-center gap-1">
                  View All <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {recentPayslips.map((payslip, idx) => {
                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  return (
                    <div key={idx} className="p-4 rounded-xl bg-muted/30 border hover:border-[#1a365d]/50 transition-colors">
                      <p className="text-sm text-muted-foreground">{monthNames[payslip.month - 1]} {payslip.year}</p>
                      <p className="text-2xl font-bold mt-1 text-green-600">{formatCurrency(payslip.net_salary)}</p>
                      <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                        <span>Gross: {formatCurrency(payslip.gross_earnings)}</span>
                        <span>Deductions: {formatCurrency(payslip.total_deductions)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // HR MANAGER / SENIOR MANAGEMENT DASHBOARD
  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="page-header flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-muted-foreground mb-1">{getGreeting()}</p>
          <h1 className="page-title">{user?.name}</h1>
        </div>
        <div className="text-right">
          <p className="text-4xl font-bold clock-display text-foreground">
            {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
          </p>
          <p className="text-muted-foreground">
            {currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
      </div>

      {/* Clock-in/out card */}
      <Card className="relative overflow-hidden border-0 bg-gradient-to-r from-[#0f2744] to-[#1a365d] text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#14b8a6]/10 rounded-full translate-y-1/2 -translate-x-1/4" />
        <CardContent className="relative py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="h-20 w-20 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                <Clock className="h-10 w-10 text-[#14b8a6]" />
              </div>
              <div>
                <p className="text-white/70 text-sm mb-1">Today's Status</p>
                {todayAttendance?.clocked_in ? (
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-sm text-white/60">In</p>
                      <p className="text-xl font-semibold">{todayAttendance.clock_in_time}</p>
                    </div>
                    {todayAttendance?.clocked_out && (
                      <>
                        <div className="h-8 w-px bg-white/20" />
                        <div>
                          <p className="text-sm text-white/60">Out</p>
                          <p className="text-xl font-semibold">{todayAttendance.clock_out_time}</p>
                        </div>
                        <div className="h-8 w-px bg-white/20" />
                        <div>
                          <p className="text-sm text-white/60">Hours</p>
                          <p className="text-xl font-semibold">{todayAttendance.work_hours}h</p>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-2xl font-semibold">Not clocked in yet</p>
                )}
              </div>
            </div>
            <div>
              {!todayAttendance?.clocked_in ? (
                <Button 
                  onClick={handleClockIn} 
                  disabled={clockLoading}
                  className="h-14 px-8 rounded-xl bg-white text-[#1a365d] hover:bg-white/90 font-semibold gap-2"
                  data-testid="clock-in-btn"
                >
                  <Play className="h-5 w-5" />
                  Clock In
                </Button>
              ) : !todayAttendance?.clocked_out ? (
                <Button 
                  onClick={handleClockOut} 
                  disabled={clockLoading}
                  className="h-14 px-8 rounded-xl bg-[#14b8a6] text-[#1A2433] hover:bg-[#14b8a6]/90 font-semibold gap-2"
                  data-testid="clock-out-btn"
                >
                  <Square className="h-5 w-5" />
                  Clock Out
                </Button>
              ) : (
                <div className="flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-500/20 text-emerald-100">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-semibold">Day Complete</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
        <Card className="stat-card border-l-4 border-l-[#1a365d]" data-testid="stat-employees">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Employees</p>
                <p className="text-4xl font-bold mt-2">{stats?.total_employees || 0}</p>
                {stats?.new_joiners > 0 && (
                  <p className="text-sm text-emerald-600 mt-2 flex items-center gap-1">
                    <TrendingUp className="h-4 w-4" />
                    +{stats.new_joiners} this month
                  </p>
                )}
              </div>
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#1a365d]/10 to-[#1a365d]/5 flex items-center justify-center">
                <Users className="h-7 w-7 text-[#1a365d]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card border-l-4 border-l-emerald-500" data-testid="stat-present">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Present Today</p>
                <p className="text-4xl font-bold mt-2">{stats?.present_today || 0}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  {stats?.absent_today || 0} absent
                </p>
              </div>
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 flex items-center justify-center">
                <CheckCircle2 className="h-7 w-7 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card border-l-4 border-l-amber-500" data-testid="stat-leaves">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending Leaves</p>
                <p className="text-4xl font-bold mt-2">{stats?.pending_leaves || 0}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Awaiting approval
                </p>
              </div>
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-amber-500/10 to-amber-500/5 flex items-center justify-center">
                <Calendar className="h-7 w-7 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {canAccessPayroll && (
          <Card className="stat-card border-l-4 border-l-purple-500" data-testid="stat-payroll">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Monthly Payroll</p>
                  <p className="text-3xl font-bold mt-2 currency">{formatCurrency(stats?.total_payroll || 0)}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Net disbursement
                  </p>
                </div>
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-purple-500/10 to-purple-500/5 flex items-center justify-center">
                  <IndianRupee className="h-7 w-7 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Charts row */}
      {canAccessReports && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Attendance Trend */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Attendance Overview</CardTitle>
              <CardDescription>Last 14 days attendance trend</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={attendanceTrend}>
                    <defs>
                      <linearGradient id="colorPresent" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1a365d" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#1a365d" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '0.75rem',
                        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)'
                      }}
                      labelFormatter={(value) => new Date(value).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="present" 
                      stroke="#1a365d" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorPresent)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Department Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">By Department</CardTitle>
              <CardDescription>Employee distribution</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={deptData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      paddingAngle={2}
                    >
                      {deptData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 space-y-2">
                {deptData.slice(0, 4).map((dept, index) => (
                  <div key={dept.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                      <span className="text-muted-foreground truncate max-w-[120px]">{dept.name}</span>
                    </div>
                    <span className="font-semibold">{dept.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick actions for HR */}
      {isHRManager && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: UserPlus, label: 'Add Employee', href: '/employees', color: 'from-[#1a365d]/10 to-[#1a365d]/5', iconColor: 'text-[#1a365d]' },
                { icon: Calendar, label: 'Review Leaves', href: '/leaves', color: 'from-amber-500/10 to-amber-500/5', iconColor: 'text-amber-600' },
                { icon: IndianRupee, label: 'Process Payroll', href: '/payroll', color: 'from-purple-500/10 to-purple-500/5', iconColor: 'text-purple-500' },
                { icon: TrendingUp, label: 'View Reports', href: '/reports', color: 'from-sky-500/10 to-sky-500/5', iconColor: 'text-sky-600' },
              ].map((action) => (
                <a
                  key={action.label}
                  href={action.href}
                  className={cn(
                    "group flex flex-col items-center justify-center gap-3 p-6 rounded-2xl",
                    "bg-gradient-to-br", action.color,
                    "border border-transparent hover:border-border",
                    "transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
                  )}
                >
                  <action.icon className={cn("h-8 w-8", action.iconColor)} />
                  <span className="text-sm font-medium text-foreground">{action.label}</span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
